import {UseSlotValidationMutationParams} from '../context/SlotManagementContext'
import {useSlotManagementContext} from '../context/useSlotManagementContext'

/**
 * Strategy hook for validating slots on-demand (mutation-based).
 * Uses implementation from context (Order Change or Order Intake).
 *
 * The actual implementation is provided by the context provider and depends on:
 * - Order Change: useOrderChangeSlotValidationMutation (single slot, checks dirty fields)
 * - Order Intake: useOrderIntakeSlotValidationMutation (multiple slots in parallel)
 *
 * Use this hook when you need to trigger validation manually (e.g., on save).
 * For automatic/interval validation, use `useSlotValidation` instead.
 *
 * Returns single mutation that validates all relevant slots using Promise.allSettled.
 *
 * @param params - Parameters for slot validation mutation
 * @param params.slotValidationMutationFn - Function that validates a single slot
 * @returns Single mutation result with canValidate flag and slot availabilities
 *
 * @example
 * ```tsx
 * // Order Change usage - validate on save
 * const {mutation, shouldValidate, slotAvailabilities} = useSlotValidationMutation({
 *   slotValidationMutationFn: (params) => validateSlot(params)
 * })
 *
 * const handleSave = async () => {
 *   if (shouldValidate) {
 *     // No params needed - built internally using useSlotRequest
 *     const result = await mutation.mutateAsync()
 *     // Check current availability after mutation
 *     console.log('Current availability:', slotAvailabilities[0].isAvailable)
 *   }
 * }
 *
 * // Order Intake usage - validate all slots on save
 * const {mutation, shouldValidate, slotAvailabilities} = useSlotValidationMutation({
 *   slotValidationMutationFn: (params) => validateSlot(params)
 * })
 *
 * const handleSave = async () => {
 *   if (shouldValidate) {
 *     // No params needed - built internally using useSlotRequests
 *     const result = await mutation.mutateAsync()
 *     console.log(`Validated ${slotAvailabilities.length} slots`)
 *     // Check all availabilities
 *     slotAvailabilities.forEach(slot => {
 *       console.log(`${slot.dateTime}: ${slot.isAvailable ? 'available' : 'unavailable'}`)
 *     })
 *   }
 * }
 * ```
 */
export const useSlotValidationMutation = (params: UseSlotValidationMutationParams) => {
  const {hooks} = useSlotManagementContext()
  return hooks.useSlotValidationMutation(params)
}
